import { FC, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";

import HtmlText from "@/sections/common/HtmlText";

// ----------------------------------------------------------------------------
// SectionHeader — the canonical eyebrow + title + subtitle block.
//
// Pulled into a shared component so every section across the site uses the
// EXACT same type scale and rhythm. Previously each section reinvented its
// header with slightly different sizes (one was text-h5/h3/h2, another was
// text-3xl/4xl/5xl, another was text-4xl/5xl/6xl) — which made the page
// feel inconsistent.
//
// Canonical scale:
//   - Eyebrow:  text-xs md:text-sm        uppercase + wide tracking + bold
//   - Title:    text-3xl md:text-4xl xl:text-5xl   font-medium
//   - Subtitle: text-base md:text-lg                regular
//
// Variants:
//   - tone="dark"   — for sections on dark backgrounds (white text on dark)
//   - tone="light"  — default, dark text on light/white background
//
// Alignment:
//   - align="center" — default; centers the whole block with max-w-3xl
//   - align="left"   — for asymmetric layouts where header sits in a column
// ----------------------------------------------------------------------------

export type ISectionHeaderProps = HTMLAttributes<HTMLElement> & {
  eyebrow?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  tone?: "light" | "dark";
  align?: "center" | "left";
};

const SectionHeader: FC<ISectionHeaderProps> = ({
  eyebrow,
  title,
  subtitle,
  tone = "light",
  align = "center",
  className,
  ...other
}) => {
  const isDark = tone === "dark";
  const isCenter = align === "center";

  return (
    <div
      className={clsx(
        isCenter && "mx-auto max-w-3xl text-center",
        className,
      )}
      {...other}
    >
      {eyebrow && (
        <p
          className={clsx(
            // Fluid: 12px at narrow viewports → 14px at wide. No breakpoint
            // jump — scales smoothly via clamp(min, preferred, max).
            "mb-4 font-bricolage-grotesque text-[clamp(0.75rem,0.4vw+0.65rem,0.875rem)] font-semibold uppercase tracking-[0.25em]",
            isDark ? "text-secondary-900" : "text-primary-900",
          )}
        >
          {eyebrow}
        </p>
      )}

      {title && (
        <h2
          className={clsx(
            // Fluid: 22px at narrow → 48px at wide. Starting point was
            // 30px which felt oversized on 320–375px viewports (titles
            // wrapped to 3-4 lines and dominated the fold). 22px keeps
            // the heading legible on phones while preserving the 48px
            // anchor on desktop.
            "font-bricolage-grotesque text-[clamp(1.375rem,3vw+0.75rem,3rem)] font-medium !leading-[1.1]",
            isDark ? "text-white" : "text-black-900",
          )}
        >
          {title}
        </h2>
      )}

      {subtitle && (
        <div
          className={clsx(
            // Fluid: 16px at narrow → 18px at wide.
            "mt-5 text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] leading-relaxed md:mt-6",
            isDark ? "text-white/70" : "text-black-700",
            isCenter && "mx-auto max-w-2xl",
          )}
        >
          {typeof subtitle === "string" ? (
            subtitle
          ) : (
            <HtmlText
              className={clsx(
                isDark
                  ? "prose-p:text-white/70 prose-strong:text-white"
                  : "prose-p:text-black-700 prose-strong:text-black-900",
                // Match the fluid scale used on the wrapper above.
                // `!` is REQUIRED to win against HtmlText's
                // `prose-p:!text-base` which is itself !important.
                "prose-p:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)]",
              )}
            >
              {subtitle}
            </HtmlText>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionHeader;
